import React from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DateTimePicker } from '@mui/x-date-pickers';
import SelectComponent from '../../../../components/select/SelectComponent';
import Iconify from '../../../../components/iconify';
import Typography from '@mui/material/Typography';
import { Divider, SaveButton, EditedText } from '../components';
import { shouldShowEdited, PROFILE_MODES } from '../profileFormConfig';

/**
 * Access section of the profile form
 * Handles: access level, validFrom, expiresAt, isAntiPassback
 */
const AccessSection = ({
  mode,
  config,
  profileData,
  initialProfileData,
  metadata,
  errors,
  handleInputChange,
  checkFieldDisabled,
  onSave,
  onDraftSave,
  isEdited,
  canEdit,
}) => {
  const { sectionSave, showDraftButton } = config;
  const showEditIndicator = shouldShowEdited(mode, 'access');
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const gridSpacing = isSmUp ? 3 : 2;
  
  // Disable all fields if user can't edit (e.g., deleted profiles)
  const isDisabled = !canEdit || (checkFieldDisabled && checkFieldDisabled('access'));

  return (
    <>
      <Grid item xs={12} marginBottom={{ xs: 2, sm: 3 }} marginTop={{ xs: 2, sm: 3 }}>
        <Divider text="Access" />
      </Grid>

      <Grid container spacing={gridSpacing}>
        {/* Gate Access */}
        {sectionSave ? (
          <>
            <Grid item sm={6} xs={9}>
              <Tooltip title="This setting allows a person to pass through turnstile during working hours only or all times" arrow placement="top">
                <Box data-profile-field="access">
                  <SelectComponent
                    id="access"
                    label="Gate Access"
                    shrink={true}
                    items={metadata.access}
                    value={profileData.access}
                    setValue={(value) => handleInputChange('access', value)}
                    error={!!errors.access}
                    helperText={errors.access || ''}
                    disabled={isDisabled}
                  />
                </Box>
              </Tooltip>
            </Grid>
            <Grid item sm={6} xs={3}>
              {initialProfileData.access !== profileData.access && (
                <EditedText />
              )}
            </Grid>
          </>
        ) : (
          <>
            <Grid item sm={8} xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }} data-profile-field="access">
                <Box sx={{ flex: 1 }}>
                  <SelectComponent
                    id="access"
                    label="Gate Access"
                    shrink={true}
                    items={metadata.access}
                    value={profileData.access}
                    setValue={(value) => handleInputChange('access', value)}
                    error={!!errors.access}
                    helperText={errors.access || ''}
                    disabled={isDisabled}
                  />
                </Box>
                <Tooltip title="This setting allows a person to pass through turnstile during working hours only or all times" arrow placement="top">
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'help', mt: 2 }}>
                    <Iconify icon="mdi:information-outline" width={20} sx={{ color: 'text.secondary' }} />
                  </Box>
                </Tooltip>
              </Box>
            </Grid>
            <Grid item sm={4} xs={0}></Grid>
          </>
        )}

        {/* Valid From */}
        {sectionSave ? (
          <>
            <Grid item sm={6} xs={9}>
              <Box data-profile-field="validFrom">
                <DateTimePicker
                  label="Valid From"
                  defaultValue={null}
                  value={profileData.validFrom}
                  onChange={(value) => handleInputChange('validFrom', value)}
                  disabled={isDisabled}
                  sx={{ width: '100%' }}
                  format="DD/MM/YYYY hh:mm a"
                  slotProps={{
                    textField: {
                      id: 'validFrom',
                      name: 'validFrom',
                      error: !!errors.validFrom,
                      helperText: errors.validFrom || '',
                    },
                  }}
                />
              </Box>
            </Grid>
            <Grid item sm={6} xs={3}>
              {initialProfileData.validFrom !== profileData.validFrom && (
                <EditedText />
              )}
            </Grid>
          </>
        ) : (
          <>
            <Grid item sm={8} xs={12}>
              <Box data-profile-field="validFrom">
                <DateTimePicker
                  label="Valid From"
                  defaultValue={null}
                  value={profileData.validFrom}
                  onChange={(value) => handleInputChange('validFrom', value)}
                  disabled={isDisabled}
                  sx={{ width: '100%' }}
                  format="DD/MM/YYYY hh:mm a"
                  slotProps={{
                    textField: {
                      id: 'validFrom',
                      name: 'validFrom',
                      error: !!errors.validFrom,
                      helperText: errors.validFrom || '',
                    },
                  }}
                />
              </Box>
            </Grid>
            <Grid item sm={4} xs={0}></Grid>
          </>
        )}

        {/* Expires At */}
        {(() => {
          const isSkillkoProfile = mode === PROFILE_MODES.SKILLKO || (sectionSave && profileData.source === 'Skillko');
          const isAutoExpiry = isSkillkoProfile && (profileData.skillkoAutomaticExpiry ?? false);
          const expiryDisabled = sectionSave ? isDisabled : false;

          return sectionSave ? (
            <>
              <Grid item sm={6} xs={9}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }} data-profile-field="expiresAt">
                  <DateTimePicker
                    label="Expires At"
                    defaultValue={null}
                    value={profileData.expiresAt}
                    onChange={(value) => handleInputChange('expiresAt', value)}
                    disabled={expiryDisabled || isAutoExpiry}
                    sx={{ width: '100%' }}
                    format="DD/MM/YYYY hh:mm a"
                    slotProps={{
                      textField: {
                        id: 'expiresAt',
                        name: 'expiresAt',
                        error: !!errors.expiresAt,
                        helperText: errors.expiresAt || '',
                      },
                    }}
                  />
                  {isSkillkoProfile && (
                    <Tooltip title="When enabled, the expiry date is automatically calculated from Skillko certificates. Uncheck to set the expiry date manually." arrow placement="top">
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, cursor: 'help' }}>
                        <Checkbox
                          checked={isAutoExpiry}
                          onChange={(e) => handleInputChange('skillkoAutomaticExpiry', e.target.checked)}
                          disabled={expiryDisabled}
                          size="small"
                        />
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap', color: 'text.secondary', textDecoration: 'underline dotted', textUnderlineOffset: '3px' }}>
                          Auto expiry
                        </Typography>
                        <Iconify icon="mdi:information-outline" width={16} sx={{ ml: 0.5, color: 'text.disabled' }} />
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              </Grid>
              <Grid item sm={6} xs={3}>
                {(initialProfileData.expiresAt !== profileData.expiresAt ||
                  initialProfileData.skillkoAutomaticExpiry !== profileData.skillkoAutomaticExpiry) && (
                  <EditedText />
                )}
              </Grid>
            </>
          ) : (
            <>
              <Grid item sm={8} xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }} data-profile-field="expiresAt">
                  <DateTimePicker
                    label="Expires At"
                    defaultValue={null}
                    value={profileData.expiresAt}
                    onChange={(value) => handleInputChange('expiresAt', value)}
                    disabled={isAutoExpiry}
                    sx={{ width: '100%' }}
                    format="DD/MM/YYYY hh:mm a"
                    slotProps={{
                      textField: {
                        id: 'expiresAt',
                        name: 'expiresAt',
                        error: !!errors.expiresAt,
                        helperText: errors.expiresAt || '',
                      },
                    }}
                  />
                  {isSkillkoProfile && (
                    <Tooltip title="When enabled, the expiry date is automatically calculated from Skillko certificates. Uncheck to set the expiry date manually." arrow placement="top">
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, cursor: 'help' }}>
                        <Checkbox
                          checked={isAutoExpiry}
                          onChange={(e) => handleInputChange('skillkoAutomaticExpiry', e.target.checked)}
                          size="small"
                        />
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap', color: 'text.secondary', textDecoration: 'underline dotted', textUnderlineOffset: '3px' }}>
                          Auto expiry
                        </Typography>
                        <Iconify icon="mdi:information-outline" width={16} sx={{ ml: 0.5, color: 'text.disabled' }} />
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              </Grid>
              <Grid item sm={4} xs={0}></Grid>
            </>
          );
        })()}

        {/* Anti-Passback */}
        {sectionSave ? (
          <>
            <Grid item sm={6} xs={9} justifyContent="left" alignItems="center">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FormControlLabel
                  labelPlacement="end"
                  sx={{ marginTop: '0.5em' }}
                  checked={profileData.isAntiPassback}
                  control={<Checkbox disabled={isDisabled} />}
                  label="Anti-Passback"
                  onChange={(e, value) => handleInputChange('isAntiPassback', value)}
                  disabled={isDisabled}
                />
                <Tooltip title="Anti-passback enabled means profile can not enter multiple times in a row" arrow placement="top">
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'help' }}>
                    <Iconify icon="mdi:information-outline" width={20} sx={{ color: 'text.secondary' }} />
                  </Box>
                </Tooltip>
              </Box>
            </Grid>
            <Grid item container sm={6} xs={3} justifyContent="space-between">
              <Grid item>
                {initialProfileData.isAntiPassback !== profileData.isAntiPassback && (
                  <EditedText />
                )}
              </Grid>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <SaveButton 
                  disabled={!isEdited} 
                  onClick={onSave}
                  sectionSave={sectionSave}
                  canEdit={canEdit}
                />
              </Box>
            </Grid>
            <Grid 
              item 
              container 
              xs={12} 
              justifyContent={{ xs: 'stretch', sm: 'flex-end' }}
              sx={{ mt: { xs: 1, sm: 0 } }}
            >
              <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                <SaveButton 
                  disabled={!isEdited} 
                  onClick={onSave}
                  sectionSave={sectionSave}
                  canEdit={canEdit}
                />
              </Box>
            </Grid>
          </>
        ) : (
          <>
            <Grid item sm={6} xs={9} justifyContent="left" alignItems="center">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FormControlLabel
                  labelPlacement="end"
                  sx={{ marginTop: '0.5em' }}
                  checked={profileData.isAntiPassback}
                  control={<Checkbox disabled={isDisabled} />}
                  label="Anti-Passback"
                  onChange={(e, value) => handleInputChange('isAntiPassback', value)}
                  disabled={isDisabled}
                />
                <Tooltip title="Anti-passback enabled means profile can not enter multiple times in a row" arrow placement="top">
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'help' }}>
                    <Iconify icon="mdi:information-outline" width={20} sx={{ color: 'text.secondary' }} />
                  </Box>
                </Tooltip>
              </Box>
            </Grid>

            {/* Save button for Create/Skillko modes */}
            <Grid 
              item 
              container 
              sm={6} 
              xs={12} 
              justifyContent={{ xs: 'stretch', sm: 'flex-end' }}
              sx={{ mt: { xs: 2, sm: 0 } }}
            >
              <SaveButton 
                onClick={onSave}
                onDraftClick={showDraftButton ? onDraftSave : undefined}
                showDraftButton={showDraftButton}
                canEdit={canEdit}
              />
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
};

export default AccessSection;

